/**
 * HTTP client for the Tablo server's cloud API (fetch-based: browser, desktop webview, Node).
 * The event stream is read with fetch (not EventSource) so the token travels in a header.
 */
import type { Project, SiteSummary } from '@elec/domain';
import type { SyncEvent, TxRequest, TxResponse } from './protocol.ts';

export type Role = 'owner' | 'editor' | 'viewer';
export interface Team {
  id: string;
  name: string;
  personal: boolean;
  role: Role;
}
export interface Me {
  user: { id: string; email: string; name: string };
  teams: Team[];
}
export interface ProjectSummary {
  id: string;
  title: string;
  updated: string;
  seq: number;
  createdBy: string;
}
export interface Member {
  id: string;
  email: string;
  name: string;
  role: Role;
}
export interface Share {
  token: string;
  url: string;
  revisionId: string | null;
  created: string;
  /** 'review': a link for an approved inspection body, who can pin remarks. */
  kind?: 'view' | 'review';
}
/** A remark an inspection body pinned on a drawing through a review link. */
export interface ReviewPin {
  id: string;
  created: string;
  author: string;
  body: string;
  sheetId: string;
  x: number;
  y: number;
  text: string;
  article: string;
  ref: string;
  /** 'imported' once the installer took it into the project's remarks. */
  status: 'new' | 'imported';
}
export interface LibraryItem {
  kind: 'symbol' | 'macro' | 'template';
  id: string;
  name: string;
  data: unknown;
  updated: string;
}

/** An autosaved copy of an unsaved project, kept in the account. */
export interface AutosaveSummary {
  projectId: string;
  name: string;
  /** Client time (ms) of the change it holds. */
  savedAt: number;
  size: number;
}

/** Sign-up waiting for the address to be confirmed. */
export interface Pending {
  pending: true;
  email: string;
}

/** The account exists but its address is not confirmed yet (login, or sign-up again). */
/** A wholesaler price list kept on the server (see services/api/src/prices.ts). */
export interface PriceSource {
  id: string;
  name: string;
  /** Last successful refresh (ISO). */
  updated: string;
  count: number;
  /** Last refresh failed (the previous prices are kept). */
  error: string;
}
export interface PriceLookup {
  sources: PriceSource[];
  /** Source id → reference (as asked) → price excl. VAT per unit. */
  prices: Record<string, Record<string, { price: number; unit: string; description: string }>>;
}

export const isNotConfirmed = (e: unknown) =>
  e instanceof CloudError && (e.status === 403 || e.status === 409) && e.message === 'email not confirmed';

export class CloudError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** What the app sends when a user reports a rule (services/api/src/ruleReports.ts). */
export interface RuleReport {
  ruleId: string;
  standard: 'rgie' | 'nfc15100' | 'nen1010';
  /** The rule was marked "to confirm" when reported. */
  verified: boolean;
  reason: 'wrong' | 'not-applicable' | 'wording' | 'other';
  /** The finding as the user read it. */
  message: string;
  comment: string;
  locale: 'fr' | 'en' | 'nl' | 'de';
  /** App version and platform, "0.12.1 web". */
  app: string;
}

export class CloudClient {
  readonly baseUrl: string;
  token: string | null;
  constructor(baseUrl: string, token: string | null = null) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.token = token;
  }

  private async req<T>(
    method: string,
    path: string,
    body?: unknown,
    raw?: { bytes: Uint8Array; type: string },
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (this.token) headers.authorization = `Bearer ${this.token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (raw) headers['content-type'] = raw.type;
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      ...(raw ? { body: raw.bytes as BodyInit } : body !== undefined ? { body: JSON.stringify(body) } : {}),
      // A weak mobile connection can leave a request hanging: give up (and retry) instead.
      signal: AbortSignal.timeout(raw ? 120_000 : 30_000),
    });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : {};
    if (!res.ok && !(res.status === 409 && path.endsWith('/tx')))
      throw new CloudError(res.status, (json as { error?: string }).error ?? res.statusText);
    return json as T;
  }

  /** Real-time cursor: where this client's pointer is on the project (null: off the drawing). */
  sendCursor(projectId: string, c: { clientId: string; sheetId: string | null; x: number; y: number }) {
    return this.req<{ ok: boolean }>('POST', `/api/projects/${projectId}/cursor`, c);
  }

  /** Client and site records: a summary of every project of the workspace. */
  async teamSites(teamId: string): Promise<SiteSummary[]> {
    return (await this.req<{ sites: SiteSummary[] }>('GET', `/api/teams/${teamId}/sites`)).sites;
  }

  /** "Report a wrong rule" from the check panel (signed in). */
  reportRule(report: RuleReport): Promise<{ id: string }> {
    return this.req('POST', '/api/rules/report', report);
  }

  // invoice numbering of a workspace (one continuous series per company)
  async invoiceSeries(teamId: string, prefix: string): Promise<{ prefix: string; next: number }> {
    return this.req('GET', `/api/teams/${teamId}/invoice-series?prefix=${encodeURIComponent(prefix)}`);
  }
  /** Takes the next number of the series; each number is given once. */
  takeInvoiceNumber(teamId: string, prefix: string): Promise<{ prefix: string; n: number; number: string }> {
    return this.req('POST', `/api/teams/${teamId}/invoice-numbers`, { prefix });
  }
  /** Owners: where the series goes on (never back to a number already given). */
  setInvoiceSeries(teamId: string, prefix: string, next: number): Promise<{ prefix: string; next: number }> {
    return this.req('PUT', `/api/teams/${teamId}/invoice-series`, { prefix, next });
  }

  /**
   * Emails an issued invoice to the client through the server: its PDF, and its Peppol file for
   * a company client (base64). Replies go to `replyTo` (the installer).
   */
  emailInvoice(mail: {
    to: string;
    replyTo: string;
    seller: string;
    number: string;
    kind: 'invoice' | 'credit-note' | 'deposit';
    total: string;
    dueDate?: string;
    message?: string;
    locale?: 'fr' | 'en' | 'nl' | 'de';
    pdf: string;
    xml?: string;
  }): Promise<{ ok: true }> {
    return this.req('POST', '/api/invoices/email', mail);
  }

  // Teamleader Focus (docs/decisions/0015)
  teamleaderStatus(): Promise<{ configured: boolean; connected: boolean; since: string | null }> {
    return this.req('GET', '/api/teamleader/status');
  }
  teamleaderConnect(): Promise<{ url: string }> {
    return this.req('POST', '/api/teamleader/connect');
  }
  /** A quote to Teamleader: the client found or added, a deal, the quotation with its lines. */
  teamleaderSendQuote(q: {
    client: {
      name: string;
      company: boolean;
      vatNumber?: string;
      email?: string;
      phone?: string;
      street?: string;
      postcode?: string;
      city?: string;
      country?: string;
    };
    title: string;
    lines: Array<{
      section: string;
      description: string;
      quantity: number;
      unitPrice: number;
      vatPct: number;
    }>;
  }): Promise<{
    dealId: string;
    quotationId: string;
    customer: { type: string; id: string; added: boolean };
  }> {
    return this.req('POST', '/api/teamleader/quotes', q);
  }

  // Billit (docs/decisions/0013, step 7)
  billitStatus(): Promise<{ configured: boolean; connected: boolean; since: string | null }> {
    return this.req('GET', '/api/billit/status');
  }
  /** The Billit login page to open; Billit sends the user back to the app once connected. */
  // ---- quotes sent for online acceptance (services/api/src/offers.ts)
  sendOffer(
    projectId: string,
    doc: OfferDoc,
    validDays: number,
  ): Promise<{ token: string; url: string; validUntil: string }> {
    return this.req('POST', '/api/offers', { projectId, doc, validDays });
  }
  offers(projectId?: string): Promise<{ offers: Offer[] }> {
    return this.req('GET', `/api/offers${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`);
  }
  withdrawOffer(token: string): Promise<{ ok: true }> {
    return this.req('DELETE', `/api/offers/${encodeURIComponent(token)}`);
  }
  /** The client's side: no account. */
  offer(token: string): Promise<Offer> {
    return this.req('GET', `/api/offers/${encodeURIComponent(token)}`);
  }
  acceptOffer(token: string, a: { name: string; signature: string; agree: true }): Promise<Offer> {
    return this.req('POST', `/api/offers/${encodeURIComponent(token)}/accept`, a);
  }
  billitConnect(): Promise<{ url: string }> {
    return this.req('POST', '/api/billit/connect');
  }
  billitDisconnect(): Promise<{ ok: true }> {
    return this.req('DELETE', '/api/billit/link');
  }
  /** Hands an issued invoice (or credit note) to Billit; `send`: over Peppol at once. `pdf`: base64. */
  billitSend(
    invoice: unknown,
    locale: 'fr' | 'en' | 'nl',
    pdf: string | undefined,
    send: boolean,
  ): Promise<{ orderId: number; sent: boolean }> {
    return this.req('POST', '/api/billit/invoices', { invoice, locale, ...(pdf ? { pdf } : {}), send });
  }
  billitOrder(orderId: number): Promise<{
    orderId: number;
    status: string | null;
    delivery: string | null;
    delivered: boolean | null;
    paid: boolean | null;
  }> {
    return this.req('GET', `/api/billit/invoices/${orderId}`);
  }

  // wholesaler prices
  async priceSources(): Promise<PriceSource[]> {
    return (await this.req<{ sources: PriceSource[] }>('GET', '/api/prices/sources')).sources;
  }
  /** Prices of these references in every source this account may see. */
  priceLookup(references: string[]): Promise<PriceLookup> {
    return this.req<PriceLookup>('POST', '/api/prices/lookup', { references });
  }

  // accounts
  /**
   * New account. On a server that sends email the answer is `{ pending: true }`: the account
   * works once its address is confirmed from the emailed link. Otherwise it signs in at once.
   */
  async register(
    email: string,
    password: string,
    name: string,
    locale: 'fr' | 'en' | 'nl' | 'de' = 'fr',
  ): Promise<Me | Pending> {
    const r = await this.req<(Me & { token: string }) | Pending>('POST', '/api/auth/register', {
      email,
      password,
      name,
      locale,
    });
    if ('pending' in r) return r;
    this.token = r.token;
    return r;
  }
  /** Confirm the address from the emailed link; signs in on this device. */
  async confirmEmail(token: string, label = ''): Promise<Me> {
    const r = await this.req<Me & { token: string }>('POST', '/api/auth/confirm', { token, label });
    this.token = r.token;
    return r;
  }
  /** Email the confirmation link again (same answer whether the account exists or not). */
  resendConfirmation = (email: string, locale: 'fr' | 'en' | 'nl' | 'de' = 'fr') =>
    this.req<{ ok: true }>('POST', '/api/auth/resend', { email, locale });
  async login(email: string, password: string, label = ''): Promise<Me> {
    const r = await this.req<Me & { token: string }>('POST', '/api/auth/login', { email, password, label });
    this.token = r.token;
    return r;
  }
  async logout(): Promise<void> {
    await this.req('POST', '/api/auth/logout').catch(() => undefined);
    this.token = null;
  }
  me = () => this.req<Me>('GET', '/api/me');
  /** Delete the account and its online data for good; the password confirms it (403 if wrong). */
  async deleteAccount(password: string, locale: 'fr' | 'en' | 'nl' | 'de' = 'fr'): Promise<void> {
    await this.req('DELETE', '/api/me', { password, locale });
    this.token = null;
  }
  /** Ask for a reset link by email (the answer is the same whether the account exists or not). */
  forgotPassword = (email: string, locale: 'fr' | 'en' | 'nl' | 'de' = 'fr') =>
    this.req<{ ok: true }>('POST', '/api/auth/forgot', { email, locale });
  /** Set a new password from the emailed link; signs in on this device, out everywhere else. */
  async resetPassword(
    token: string,
    password: string,
    locale: 'fr' | 'en' | 'nl' | 'de' = 'fr',
    label = '',
  ): Promise<Me> {
    const r = await this.req<Me & { token: string }>('POST', '/api/auth/reset', {
      token,
      password,
      locale,
      label,
    });
    this.token = r.token;
    return r;
  }

  // workspaces
  createTeam = (name: string) => this.req<Team>('POST', '/api/teams', { name });
  members = async (teamId: string) =>
    (await this.req<{ members: Member[] }>('GET', `/api/teams/${teamId}/members`)).members;
  addMember = (teamId: string, email: string, role: Role) =>
    this.req('POST', `/api/teams/${teamId}/members`, { email, role });
  setRole = (teamId: string, userId: string, role: Role) =>
    this.req('PATCH', `/api/teams/${teamId}/members/${userId}`, { role });
  removeMember = (teamId: string, userId: string) =>
    this.req('DELETE', `/api/teams/${teamId}/members/${userId}`);

  // projects
  projects = async (teamId: string) =>
    (await this.req<{ projects: ProjectSummary[] }>('GET', `/api/teams/${teamId}/projects`)).projects;
  createProject = (teamId: string, project: Project) =>
    this.req<{ id: string; seq: number }>('POST', '/api/projects', { teamId, project });
  openProject = (id: string) =>
    this.req<{ project: Project; seq: number; role: Role; teamId: string }>('GET', `/api/projects/${id}`);
  deleteProject = (id: string) => this.req('DELETE', `/api/projects/${id}`);
  sendTx = (projectId: string, req: TxRequest) =>
    this.req<TxResponse>('POST', `/api/projects/${projectId}/tx`, req);
  listAssets = async (projectId: string) =>
    (await this.req<{ assets: string[] }>('GET', `/api/projects/${projectId}/assets`)).assets;
  putAsset = (projectId: string, id: string, bytes: Uint8Array, type: string) =>
    this.req('PUT', `/api/projects/${projectId}/assets/${encodeURIComponent(id)}`, undefined, {
      bytes,
      type,
    });
  async getAsset(path: string): Promise<{ bytes: Uint8Array; type: string }> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      headers: this.token ? { authorization: `Bearer ${this.token}` } : {},
    });
    if (!res.ok) throw new CloudError(res.status, res.statusText);
    return { bytes: new Uint8Array(await res.arrayBuffer()), type: res.headers.get('content-type') ?? '' };
  }
  asset = (projectId: string, id: string) =>
    this.getAsset(`/api/projects/${projectId}/assets/${encodeURIComponent(id)}`);

  // sharing
  createShare = (projectId: string, revisionId: string | null = null, kind: 'view' | 'review' = 'view') =>
    this.req<{ token: string; url: string }>('POST', `/api/projects/${projectId}/shares`, {
      revisionId,
      kind,
    });
  shares = async (projectId: string) =>
    (await this.req<{ shares: Share[] }>('GET', `/api/projects/${projectId}/shares`)).shares;
  revokeShare = (token: string) => this.req('DELETE', `/api/shares/${token}`);
  shared = (token: string) =>
    this.req<{
      project: Project;
      revision: string | null;
      projectId: string;
      kind?: 'view' | 'review';
      pins?: ReviewPin[];
    }>('GET', `/api/shared/${token}`);
  /** On a review link: pin a remark on a drawing (no account). */
  addPin = (
    token: string,
    pin: Pick<ReviewPin, 'author' | 'body' | 'sheetId' | 'x' | 'y' | 'text' | 'article' | 'ref'>,
  ) => this.req<ReviewPin>('POST', `/api/shared/${token}/pins`, pin);
  deletePin = (token: string, id: string) =>
    this.req<{ ok: true }>('DELETE', `/api/shared/${token}/pins/${encodeURIComponent(id)}`);
  /** The installer: remarks pinned through the project's review links. */
  projectPins = async (projectId: string) =>
    (await this.req<{ pins: ReviewPin[] }>('GET', `/api/projects/${projectId}/pins`)).pins;
  pinsImported = (projectId: string, ids: string[]) =>
    this.req<{ ok: true }>('POST', `/api/projects/${projectId}/pins/imported`, { ids });
  sharedAsset = (token: string, id: string) =>
    this.getAsset(`/api/shared/${token}/assets/${encodeURIComponent(id)}`);

  // libraries
  library = async (teamId: string) =>
    (await this.req<{ items: LibraryItem[] }>('GET', `/api/teams/${teamId}/library`)).items;
  putLibrary = (teamId: string, kind: LibraryItem['kind'], id: string, name: string, data: unknown) =>
    this.req('PUT', `/api/teams/${teamId}/library/${kind}/${encodeURIComponent(id)}`, { name, data });
  deleteLibrary = (teamId: string, kind: LibraryItem['kind'], id: string) =>
    this.req('DELETE', `/api/teams/${teamId}/library/${kind}/${encodeURIComponent(id)}`);

  // autosaves (unsaved work of the signed-in user, recoverable on any device)
  autosaves = async () =>
    (await this.req<{ autosaves: AutosaveSummary[] }>('GET', '/api/autosaves')).autosaves;
  putAutosave = (projectId: string, name: string, savedAt: number, bytes: Uint8Array) =>
    this.req(
      'PUT',
      `/api/autosaves/${encodeURIComponent(projectId)}?name=${encodeURIComponent(name)}&savedAt=${savedAt}`,
      undefined,
      { bytes, type: 'application/octet-stream' },
    );
  autosave = async (projectId: string) =>
    (await this.getAsset(`/api/autosaves/${encodeURIComponent(projectId)}`)).bytes;
  deleteAutosave = (projectId: string) =>
    this.req('DELETE', `/api/autosaves/${encodeURIComponent(projectId)}`);

  /**
   * Follow a project's events. Reconnects after a drop (1 s, then up to 30 s), resuming from
   * `since()`. Returns a function that stops it.
   */
  connect(
    projectId: string,
    opts: {
      clientId: string;
      since(): number;
      onEvent(ev: SyncEvent): void;
      onStatus?(online: boolean): void;
    },
  ): () => void {
    let stopped = false;
    let ctrl: AbortController | null = null;
    let delay = 1000;
    // Back online (phone leaves a dead zone): reconnect now rather than after the backoff.
    let wake: (() => void) | null = null;
    /** An "online" signal came while a request was in flight: do not wait before the next one. */
    let kicked = false;
    const onOnline = () => {
      delay = 1000;
      if (wake) wake();
      else kicked = true;
    };
    const win = typeof window !== 'undefined' ? window : undefined;
    win?.addEventListener('online', onOnline);
    const run = async () => {
      while (!stopped) {
        ctrl = new AbortController();
        try {
          const res = await fetch(
            `${this.baseUrl}/api/projects/${projectId}/events?since=${opts.since()}&clientId=${encodeURIComponent(opts.clientId)}`,
            {
              headers: this.token ? { authorization: `Bearer ${this.token}` } : {},
              signal: ctrl.signal,
            },
          );
          if (res.status === 401 || res.status === 403 || res.status === 404) {
            opts.onStatus?.(false);
            return;
          }
          if (!res.ok || !res.body) throw new Error(String(res.status));
          opts.onStatus?.(true);
          delay = 1000;
          const reader = res.body.getReader();
          const dec = new TextDecoder();
          let buf = '';
          // The server pings every 25 s: silence means the connection died without an error
          // (network change on a phone). Drop it and reconnect.
          let last = Date.now();
          const watchdog = setInterval(() => {
            if (Date.now() - last > 60_000) ctrl?.abort();
          }, 10_000);
          try {
            for (;;) {
              const { value, done } = await reader.read();
              last = Date.now();
              if (done) break;
              buf += dec.decode(value, { stream: true });
              let i: number;
              while ((i = buf.indexOf('\n\n')) >= 0) {
                const chunk = buf.slice(0, i);
                buf = buf.slice(i + 2);
                const data = chunk
                  .split('\n')
                  .filter((l) => l.startsWith('data: '))
                  .map((l) => l.slice(6))
                  .join('\n');
                if (data) opts.onEvent(JSON.parse(data) as SyncEvent);
              }
            }
          } finally {
            clearInterval(watchdog);
          }
        } catch {
          /* dropped: retry */
        }
        if (stopped) return;
        opts.onStatus?.(false);
        if (kicked) {
          kicked = false;
          continue;
        }
        await new Promise<void>((r) => {
          const t = setTimeout(r, delay);
          wake = () => {
            clearTimeout(t);
            r();
          };
        });
        wake = null;
        delay = Math.min(delay * 2, 30_000);
      }
    };
    void run();
    return () => {
      stopped = true;
      ctrl?.abort();
      wake?.();
      win?.removeEventListener('online', onOnline);
    };
  }
}

/** A quote as the client sees it online: a frozen copy, not the project. */
export interface OfferDoc {
  title: string;
  number: string;
  /** ISO date. */
  date: string;
  locale: 'fr' | 'en' | 'nl' | 'de';
  seller: {
    name: string;
    address: string;
    email: string;
    phone: string;
    vatNumber: string;
    iban: string;
    bic: string;
  };
  client: { name: string; address: string; email: string; phone: string; vatNumber: string };
  site: string;
  lines: Array<{
    section: 'material' | 'cable' | 'labour';
    reference: string;
    description: string;
    quantity: number;
    unit: string;
    unitPrice?: number;
  }>;
  vatPct: number;
  discountPct: number;
  subtotal: number;
  vat: number;
  total: number;
  /** % of the total asked as a deposit on acceptance. */
  depositPct: number;
  paymentRef: string;
  note: string;
}

export interface Offer {
  token: string;
  url: string;
  projectId?: string;
  created: string;
  validUntil: string;
  status: 'open' | 'accepted' | 'withdrawn' | 'expired';
  doc: OfferDoc;
  /** Signature (PNG, base64): for the installer only. */
  accepted?: { at: string; name: string; signature?: string };
}
